import { isEmpty } from "@medplum/core";
import { Task } from "@medplum/fhirtypes";

import { createEntityAdapter, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ChecklistTaskStatus } from "@/constants/checklist";
import { ChecklistTask } from "@/types/checklist-task";

import { RootState } from "..";

const checklistsAdapter = createEntityAdapter({
  selectId: (checklist: ChecklistTask) => checklist.id!,
});

interface ChecklistsState {
  entities: ReturnType<typeof checklistsAdapter.getInitialState>["entities"];
  ids: ReturnType<typeof checklistsAdapter.getInitialState>["ids"];
  selectedChecklist?: ChecklistTask | undefined | null;
  selectedTask?: Task | undefined | null;
}

const initialState: ChecklistsState = {
  ...checklistsAdapter.getInitialState(),
  selectedChecklist: null,
  selectedTask: null,
};

const reducers = {
  loadChecklists: checklistsAdapter.setAll,
  addChecklist: checklistsAdapter.addOne,
  removeChecklist: checklistsAdapter.removeOne,
  updateChecklist: checklistsAdapter.updateOne,
  upsertChecklist: checklistsAdapter.upsertOne,
  setSelectedChecklist: (
    state: ChecklistsState,
    action: PayloadAction<string | undefined | null>,
  ) => {
    state.selectedChecklist = action.payload ? state.entities[action.payload] : null;
  },
  setSelectedTask: (state: ChecklistsState, action: PayloadAction<Task>) => {
    state.selectedTask = action.payload;
  },
  addChecklistTask: (
    state: ChecklistsState,
    action: PayloadAction<{ checklistId: string; task: Task }>,
  ) => {
    const { checklistId, task } = action.payload;
    state.entities[checklistId].tasks[task.id!] = task;
    if (state.selectedChecklist?.id === checklistId) {
      state.selectedChecklist = state.entities[checklistId];
    }
  },
  removeChecklistTask: (
    state: ChecklistsState,
    action: PayloadAction<{ checklistId: string; taskId: string }>,
  ) => {
    const { checklistId, taskId } = action.payload;
    delete state.entities[checklistId].tasks[taskId];
    if (state.selectedChecklist?.id === checklistId) {
      state.selectedChecklist = state.entities[checklistId];
    }
  },
  completeChecklistTasksAction: (
    state: ChecklistsState,
    action: PayloadAction<{ checklistIds: string[] }>,
  ) => {
    const { checklistIds } = action.payload;

    checklistIds.forEach((checklistId: string) => {
      Object.values(state.entities[checklistId].tasks ?? {}).forEach((task) => {
        task.status = ChecklistTaskStatus.Completed;
      });
    });
  },
  searchAndUpdateChecklistTasks: (state: ChecklistsState, action: PayloadAction<Task[]>) => {
    const tasks = action.payload;

    for (const checklist of Object.values(state.entities)) {
      for (const task of tasks) {
        if (task.id && task.id in checklist.tasks) {
          checklist.tasks[task.id] = task;
        }
      }
    }
  },
};

export const checklistsSlice = createSlice({
  name: "checklists",
  initialState,
  reducers,
});

export const {
  loadChecklists,
  addChecklist,
  removeChecklist,
  updateChecklist,
  upsertChecklist,
  setSelectedChecklist,
  removeChecklistTask,
  addChecklistTask,
  setSelectedTask,
  completeChecklistTasksAction,
  searchAndUpdateChecklistTasks,
} = checklistsSlice.actions;

const selectChecklistsEntities = (state: RootState) => state.checklists.entities;

export const getSelectedChecklist = (state: RootState) => state.checklists.selectedChecklist;

export const getSelectedTask = (state: RootState) => state.checklists.selectedTask;

export const selectChecklistList = createSelector(
  [selectChecklistsEntities],
  (checklistsEntities): ChecklistTask[] => Object.values(checklistsEntities),
);

export const getActiveChecklist = createSelector([selectChecklistList], (checklists) =>
  checklists.filter(
    (checklist) =>
      isEmpty(checklist.tasks) ||
      Object.values(checklist.tasks).some((task) => task.status === ChecklistTaskStatus.InProgress),
  ),
);

export const getCompletedChecklist = createSelector([selectChecklistList], (checklists) =>
  checklists.filter(
    (checklist) =>
      !isEmpty(checklist.tasks) &&
      Object.values(checklist.tasks).every((task) => task.status === ChecklistTaskStatus.Completed),
  ),
);

export const getChecklistById = createSelector(
  [selectChecklistsEntities],
  (checklistsEntities) => (checklistId: string) => checklistsEntities[checklistId || ""],
);

export const getUncompletedTasksByChecklistId = createSelector(
  [selectChecklistsEntities],
  (checklistsEntities) => (checklistId: string) =>
    Object.values(checklistsEntities[checklistId || ""]?.tasks).filter(
      (task) => task.status !== ChecklistTaskStatus.Completed,
    ),
);

export const getAllAssociatedChecklistTasks = createSelector(
  [selectChecklistList],
  (checklists) => (ownerId?: string) =>
    checklists.flatMap((checklist) =>
      Object.values(checklist.tasks).filter(
        (task) =>
          task.owner?.reference?.split("/")[1] === (ownerId ?? "") &&
          task.status !== ChecklistTaskStatus.Completed,
      ),
    ),
);

export default checklistsSlice.reducer;
